
import { GlobalContext } from 'components/GlobalContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { Col, Nav, Navbar, Row, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import IHealthRiskAssessment from 'models/sql/IHealthRiskAssessment';
import IEquipment from 'models/sql/IEquipment';
import { formatDate, formatDateTime } from 'utilities/StringUtility';
import MiscService from 'services/MiscService';
import IFireDepartment from 'models/sql/IFireDepartment';

import './styles/DeptDashboard.scss';
import { getStatus, getStatusDate } from 'utilities/EquipmentUtility';
import { getBaseDomain } from 'utilities/MiscUtilities';

const DeptDashboardIndex = () => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();
    const [hras, setHras] = useState<IHealthRiskAssessment[]>([]);
    const [dept, setDept] = useState<IFireDepartment>();
    const [airCards, setAirCards] = useState<IEquipment[]>([]);
    const [showLoadError, setShowLoadError] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        globalContext.setSecondaryNav(<>
            <Navbar.Brand><strong>Department Dashboard</strong></Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
                <Nav className="me-auto">
                </Nav>
            </Navbar.Collapse>
        </>);

        (async () => {
            setLoading(true);

            try {
                const accessToken = await globalContext.getAccessTokenAsync();
                const result = await MiscService.GetDeptPortalDataAsync(accessToken);

                if (result) {
                    setDept(result.dept);
                    setHras(result.hras);
                    setAirCards(result.airCards);
                }
            }
            catch (e) {
                setShowLoadError(true);
            }

            setLoading(false);
        })();
    }, []);


    if (showLoadError) {
        return (
            <div className="error-message">
                <h2>There seems to be an error with your access rights, please notify your contact at TFTHealth for help.</h2>
            </div>
        );
    }

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    if (loading) {
        return (
            <div className="loading-display">
                <div className="content">
                    <h2>
                        <Spinner animation="border" role="status"></Spinner>
                        &nbsp;Loading...
                    </h2>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="ts-container">
                <div className="mb-07">
                    <div className="ts-header gap-3 mt-5 mb-05">
                        <div className="d-flex justify-content-between">
                            <h1 className="ts-fs-30 fw-semibold mb-0">
                                {
                                    dept &&
                                    <a href={`https://${getBaseDomain()}/Public/HRAReport?did=${dept.fireDepartmentId}`} target="_blank">Health Risk Assessment Report</a>
                                }
                            </h1>
                            <div className="d-none d-lg-flex gap-03">
                                <button
                                    className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14"
                                    id="shareReportButton">
                                    <img
                                        width="19"
                                        src="/Images/Dashboard/icons/share-05.svg"
                                        alt="..." />
                                    Share
                                </button>
                            </div>
                        </div>
                    </div>

                    <ul className="nav nav-tabs d-lg-none" id="myTab" role="tablist">
                    </ul>

                    <hr className="d-none d-lg-block" />
                </div>
            </div>
            <main>
                <div className="ts-container">
                    <div className="ts-main-layout tab-content mb-5" id="myTabContent">
                        <div className="tab-pane fade"
                            id="Contact-Details-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Contact-Details-tab"
                            tabIndex={0}>
                            <div className="ts-profile-card ts-bg-white">
                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        Department
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{dept?.fireDeptName}</p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        Address
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">
                                        {dept?.hqaddr1}{dept?.hqaddr2 && " " + dept?.hqaddr2 + " "}<br />
                                        {dept?.hqcity}, {dept?.hqstate} {dept?.hqzip} {dept?.hqCountry}
                                    </p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        County
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{dept?.county}</p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        Stations
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{dept?.numberOfStations}</p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <button
                                        className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14">
                                        Edit
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="tab-pane fade show active"
                            id="Overview-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Overview-tab"
                            tabIndex={0}>
                            <div>
                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header">
                                        <h1 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            HRA & AirCard Stats
                                        </h1>
                                    </div>
                                    <div className="ts-custom-card__body">
                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-20 gap-sm-5">
                                            Nothing here yet
                                        </div>
                                    </div>
                                </div>

                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header">
                                        <h1 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            AirCards
                                        </h1>
                                    </div>
                                    <div className="ts-custom-card__body">
                                        <Table className="lookup-table">
                                            <thead>
                                                <tr>
                                                    <th>Entry Date</th>
                                                    <th>Department</th>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Score</th>
                                                    <th>Status</th>
                                                    <th>Status Date</th>
                                                    <th>Serial</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    !airCards?.length &&
                                                    <tr>
                                                        <td colSpan={5} className="text-center">
                                                            <strong>No AirCards Found</strong>
                                                        </td>
                                                    </tr>
                                                }
                                                {
                                                    airCards?.map((e, index) =>
                                                        <tr
                                                            key={index}
                                                            style={{ cursor: "pointer" }}
                                                            className={(e.demoRequestDate && !e.demoStartDate) ? "table-action-required" : ""}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                            }}><td>
                                                                {formatDate(e.created)}
                                                            </td>
                                                            <td>
                                                                {e.fireDepartment?.fireDeptName ?? e.departmentName}
                                                            </td>
                                                            <td>
                                                                {e.registrationName}
                                                            </td>
                                                            <td>
                                                                {e.registrationEmail}
                                                            </td>
                                                            <td>
                                                                {e.score}
                                                            </td>
                                                            <td>
                                                                {getStatus(e)}
                                                            </td>
                                                            <td>
                                                                {getStatusDate(e)}
                                                            </td>
                                                            <td>
                                                                {e.serialNumber}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>

                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header">
                                        <h1 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            Health Risk Assessments
                                        </h1>
                                    </div>
                                    <div className="ts-custom-card__body">

                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Entry Date</th>
                                                    <th>Department</th>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Score</th>
                                                    <th>Report #</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    !hras?.length &&
                                                    <tr>
                                                        <td colSpan={5} className="text-center">
                                                            <strong>No Health Risk Assessments Found</strong>
                                                        </td>
                                                    </tr>
                                                }
                                                {
                                                    hras?.map((e, index) =>
                                                        <tr
                                                            key={index}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                            }}>
                                                            <td>
                                                                {formatDateTime(e.submissionDate)}
                                                            </td>
                                                            <td>
                                                                {e.fireDepartment?.fireDeptName ?? ""}
                                                            </td>
                                                            <td>
                                                                {e.userName}
                                                            </td>
                                                            <td>
                                                                {e.userEmail}
                                                            </td>
                                                            <td>
                                                                {calcScore(e)}
                                                            </td>
                                                            <td>
                                                                {e.code}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div className="tab-pane fade"
                            id="Share-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Share-tab"
                            tabIndex={0}>
                            <form action="#">
                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header">
                                        <h1 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0" >
                                            Share Report
                                        </h1>
                                    </div>
                                    <div className="ts-custom-card__body">
                                        <div id="shareContent">
                                            <p>
                                                Please provide the name and email address of the person you want this report shared with and we will email them on your behalf.
                                            </p>
                                            <div className="mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control ts-form-control"
                                                    id="shareFullName"
                                                    placeholder="Full Name" />
                                            </div>
                                            <div className="mb-3">
                                                <input
                                                    type="email"
                                                    className="form-control ts-form-control"
                                                    id="shareEmail"
                                                    placeholder="Your email address" />
                                            </div>

                                            <button className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14 px-5" id="shareSubmit">Send</button>
                                        </div>
                                        <div id="shareDialogue" style={{ "display": "none" }}>
                                            <h2 id="shareDialogueTitle"></h2>
                                            <p id="shareDialogueDescription"></p>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </main >
        </>
    );
}

export default DeptDashboardIndex;